import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import {
  AttentionBoxWithIcon,
  Button,
  DialogContentText,
  Modal,
  _palette
} from '@p81-common/ui';

import { links } from 'data/constants';

import type { FeatureFlagRow } from '../helpers/types';

interface UnassignConfirmModalProps {
  flag: FeatureFlagRow | null;
  tenantName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Every measurement here comes from the DS and is deliberately NOT overridden:
 *
 *   Modal paper        width 432, radius 8
 *   MuiDialogTitle     padding 24/24/0/24, children as Typography variant="h3"
 *   mainIconContainer  64x64 white disc, 8px secondary[100] ring, margin-bottom 24
 *   MuiDialogContent   padding 8/24/24  (8 on top, not 24 — the DS rhythm is
 *                      24 / 8 / 0 / 24, so title and body sit 8px apart)
 *   DialogContentText  body2 14/400, primary[500], margin-bottom 12
 *   AttentionBox       warning[200] fill, padding 8/16, caption1 12/400
 *   MuiDialogActions   padding 0/24/24, siblings spaced 16 apart
 */
const UnassignConfirmModal = ({
  flag,
  tenantName,
  onConfirm,
  onCancel,
  isLoading = false
}: UnassignConfirmModalProps) => {
  const { t } = useTranslation(['activity']);

  return (
    <Modal
      id="unassignFeatureFlagModal"
      open={Boolean(flag)}
      onClose={onCancel}
      dialogTitleProps={{
        children: t('activity:platform.UNASSIGN_FEATURE_FLAG'),
        icon: {
          src: `${links.imagesBaseUrl}/icons2/icon_alert.svg`,
          // The DS paints this glyph primary[400] grey. Red is a deliberate
          // override for a destructive change against a live tenant —
          // flagged, not hidden.
          color: _palette.error[500]
        }
      }}
      dialogContentProps={{
        children: (
          <>
            {/* Labelled lines rather than a sentence, so the flag and the
                tenant are each scannable on their own. */}
            <DialogContentText>
              {`${t('activity:platform.FEATURE_LABEL')}: ${flag?.name || ''}`}
            </DialogContentText>
            <DialogContentText>
              {`${t('activity:platform.TENANT_LABEL')}: ${tenantName}`}
            </DialogContentText>
            <AttentionBoxWithIcon
              content={t('activity:platform.UNASSIGN_WARNING')}
            />
          </>
        )
      }}
      dialogActionsProps={{
        children: (
          <>
            {/* containedSecondary, not variant="text": the DS gives text
                buttons `height: auto` with `padding: 16px 0`, standing them
                ~53px tall beside the 40px confirm. */}
            <Button color="secondary" onClick={onCancel} disabled={isLoading}>
              {t('activity:common.CANCEL')}
            </Button>
            <Button
              color="error"
              onClick={onConfirm}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
            >
              {t('activity:platform.UNASSIGN')}
            </Button>
          </>
        )
      }}
    />
  );
};

export default UnassignConfirmModal;
